/**
 * SCD — Fiche débrief PDF (A4, 1 page) remise à chaque équipe en fin de
 * scénario. Même style que les autres PDF du jeu (jsPDF).
 *
 * generateDebriefPDF({ companyName, scenarioNom, resultat, holderName })
 *   resultat = { treasury, caCumul, satisfaction, noteAvis?, nbMagasins,
 *                caWebCumul?, score, debrief: { pointsForts, aTravailler, conseils } }
 */
import { jsPDF } from 'jspdf';
import { formatMoneyForPdf } from './formatMoneyForPdf';

/** Charge une image (chemin public ou dataURL) et la convertit en PNG dataURL. */
function loadLogoPng(src) {
  return new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        const s = 256;
        c.width = s; c.height = s;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, s, s);
        const ratio = Math.min(s / img.width, s / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        ctx.drawImage(img, (s - w) / 2, (s - h) / 2, w, h);
        resolve(c.toDataURL('image/png'));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const C = {
  primary: '#7C2D12',
  accent: '#E2704A',
  text: '#1F2937',
  muted: '#6B7280',
  light: '#F3E8E2',
  positive: '#059669',
  warning: '#B45309',
  info: '#0369A1',
};

const fmtEur = (v) => `${formatMoneyForPdf(v)} EUR`;

function sectionList(doc, x, y, width, title, color, items) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(color);
  doc.text(title, x, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(C.text);
  (items || []).forEach((it) => {
    const lines = doc.splitTextToSize(`• ${it}`, width);
    if (y + lines.length * 4.6 > 272) { doc.addPage(); y = 20; }
    doc.text(lines, x, y);
    y += lines.length * 4.6 + 1.6;
  });
  if (!items || items.length === 0) {
    doc.setTextColor(C.muted);
    doc.text('—', x, y);
    y += 6;
  }
  return y + 3;
}

export default async function generateDebriefPDF({ companyName, scenarioNom, resultat, holderName, logoSrc = null, output = 'save' }) {
  const r = resultat || {};
  const d = r.debrief || {};
  const logoPng = await loadLogoPng(logoSrc);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 16;
  const cw = W - M * 2;

  // ── En-tête ──
  doc.setFillColor(C.primary);
  doc.rect(0, 0, W, 30, 'F');
  if (logoPng) {
    doc.setFillColor('#FFFFFF');
    doc.roundedRect(W - M - 20, 5, 20, 20, 2.5, 2.5, 'F');
    doc.addImage(logoPng, 'PNG', W - M - 18.5, 6.5, 17, 17);
  }
  doc.setTextColor('#FFFFFF');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('SCD — Débrief de fin de scénario', M, 13);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${companyName || 'Votre boutique'}${holderName ? `  ·  Équipe : ${holderName}` : ''}`, M, 20);
  doc.text(`Scénario : ${scenarioNom || '—'}`, M, 25.5);

  // ── KPI ──
  let y = 40;
  const kpis = [
    ['Trésorerie finale', fmtEur(r.treasury || 0)],
    ['CA cumulé', fmtEur(r.caCumul || 0)],
    ['Satisfaction clients', `${Math.round(r.satisfaction || 0)}/100`],
    ['Points de vente', String(r.nbMagasins || 1)],
    ['CA web cumulé', fmtEur(r.caWebCumul || 0)],
    ['Score composite', String(r.score ?? '—')],
  ];
  const colW = cw / 3;
  kpis.forEach((k, i) => {
    const x = M + (i % 3) * colW;
    const yy = y + Math.floor(i / 3) * 18;
    doc.setFillColor(C.light);
    doc.roundedRect(x, yy, colW - 4, 15, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(C.muted);
    doc.text(k[0].toUpperCase(), x + 4, yy + 5.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(k[0] === 'Score composite' ? C.accent : C.text);
    doc.text(k[1], x + 4, yy + 11.5);
  });
  y += 42;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(C.muted);
  doc.text('Score = 0,4 x (trésorerie en kEUR) + 0,4 x (CA cumulé en kEUR) + 0,2 x satisfaction', M, y);
  y += 9;

  // ── SCD R3 — Investissements d'amélioration du magasin ──
  const eq = r.equipements;
  if (eq) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.primary);
    doc.text('INVESTISSEMENTS MAGASIN', M, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(C.text);
    if (!eq.nbTotal) {
      doc.setTextColor(C.muted);
      doc.text('Aucune amélioration achetée pendant le scénario.', M, y);
      y += 6;
    } else {
      doc.text(`${eq.nbTotal} amélioration(s) · ${fmtEur(eq.totalInvest)} investis · entretien ${fmtEur(eq.entretienMois)}/mois`, M, y);
      y += 5.5;
      (eq.parMagasin || []).filter(m => m.nb > 0).slice(0, 3).forEach((m) => {
        const lines = doc.splitTextToSize(`• ${m.nom} : ${m.liste.join(', ')} (${fmtEur(m.invest)})`, cw);
        doc.text(lines, M, y);
        y += lines.length * 4.6 + 1.2;
      });
    }
    y += 4;
  }

  // ── Réseau de franchise : conditions, revenus et accompagnement ──
  const ensure = (need) => { if (y + need > 272) { doc.addPage(); y = 20; } };
  const fr = r.franchise;
  if (fr) {
    ensure(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.primary);
    doc.text('RÉSEAU DE FRANCHISE', M, y);
    y += 6;
    const politique = fr.droitEntree <= 5000 && fr.redevancePct <= 2 ? 'très attractive (volume)'
      : fr.droitEntree >= 30000 || fr.redevancePct >= 8 ? 'sélective (marge par contrat)'
      : 'équilibrée';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(C.text);
    doc.text(`Conditions choisies : droit d'entrée ${fmtEur(fr.droitEntree)} · redevance ${fr.redevancePct} % du résultat — politique ${politique}`, M, y);
    y += 5.5;
    doc.text(`${fr.nbFranchises} franchisé(s)${fr.villes && fr.villes.length ? ` (${fr.villes.slice(0, 6).join(', ')})` : ''} · droits d'entrée encaissés : ${fmtEur(fr.totalDroitsEntree)} · redevances cumulées : ${fmtEur(fr.totalRedevances)}`, M, y);
    y += 5.5;
    if (fr.aides && fr.aides.demandes > 0) {
      doc.text(`Demandes d'aide des franchisés : ${fr.aides.demandes} — ${fr.aides.financees} accompagnée(s), ${fr.aides.refusees} refusée(s)/ignorée(s)`, M, y);
      y += 5.5;
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(C.muted);
    const lecture = fr.nbFranchises === 0
      ? "Lecture : réseau structuré mais aucun contrat signé — des conditions plus douces auraient accéléré le recrutement."
      : fr.aides && fr.aides.refusees > fr.aides.financees
        ? "Lecture : un franchiseur qui n'accompagne pas ses franchisés fragilise ses redevances — la franchise est un partenariat, pas une rente."
        : "Lecture : arbitrage classique du franchiseur — conditions douces = réseau rapide, conditions dures = contrats rares mais rentables. L'accompagnement des franchisés protège les redevances.";
    const lecLines = doc.splitTextToSize(lecture, cw);
    ensure(lecLines.length * 4.4 + 6);
    doc.text(lecLines, M, y);
    y += lecLines.length * 4.4 + 4;
  }

  // ── Dynamique groupe : siège, fonctions support et impacts chiffrés ──
  const gp = r.groupe;
  if (gp) {
    ensure(34);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.primary);
    doc.text('DYNAMIQUE GROUPE — FONCTIONS SUPPORT', M, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(C.text);
    const investNoms = (gp.invests || []).map(i => i.nom).filter(n => !/Siège/i.test(n));
    const ligneSiege = `${gp.siege ? 'Siège social inauguré' : 'Pas de siège social'}${investNoms.length ? ` · investissements groupe : ${investNoms.join(', ')}` : ''}`;
    const lsLines = doc.splitTextToSize(ligneSiege, cw);
    doc.text(lsLines, M, y);
    y += lsLines.length * 4.6 + 1;
    if ((gp.fonctions || []).length > 0) {
      doc.text(`${gp.fonctions.length} fonction(s) support recrutée(s) — structure : ${fmtEur(gp.coutMensuel)}/mois`, M, y);
      y += 5.5;
      gp.fonctions.slice(0, 8).forEach((f) => {
        ensure(6);
        doc.text(`• ${f.nom} — ${f.role} (${fmtEur(f.cout)}/mois)`, M + 2, y);
        y += 4.6;
      });
      const impacts = [
        gp.caPct > 0 ? `+${gp.caPct} % CA réseau` : null,
        gp.achatPct > 0 ? `-${gp.achatPct} % prix d'achat` : null,
        gp.massePct > 0 ? `-${gp.massePct} % masse salariale` : null,
        gp.fiabilitePct > 0 ? `-${gp.fiabilitePct} % retards fournisseurs` : null,
        gp.satMois > 0 ? `+${gp.satMois} satisfaction/mois` : null,
      ].filter(Boolean);
      if (impacts.length > 0) {
        const impactsTxt = `Impact cumulé sur le réseau : ${impacts.join(' · ')}`;
        const impLines = doc.splitTextToSize(impactsTxt, cw);
        ensure(impLines.length * 4.6 + 3);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(C.positive);
        doc.text(impLines, M, y);
        y += impLines.length * 4.6 + 1;
      }
    } else {
      doc.setTextColor(C.muted);
      doc.text('Aucune fonction support recrutée pendant le scénario.', M, y);
      y += 5.5;
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(C.muted);
    const lectureGp = (gp.fonctions || []).length === 0
      ? "Lecture : un groupe sans fonctions support reste une collection de magasins — le siège n'a de valeur que s'il est peuplé."
      : "Lecture : les fonctions support sont un coût fixe dilué par la croissance — chaque recrue doit couvrir son salaire par son effet réseau (CA, achats, masse salariale).";
    const lgLines = doc.splitTextToSize(lectureGp, cw);
    ensure(lgLines.length * 4.4 + 6);
    doc.text(lgLines, M, y);
    y += lgLines.length * 4.4 + 4;
  }

  // ── 03/07 — Merchandising : planogramme, parcours client et impact CA ──
  const mc = r.merchandising;
  if (mc) {
    ensure(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.primary);
    doc.text('MERCHANDISING — FACING & PARCOURS CLIENT', M, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(C.text);
    if (!mc.configure) {
      doc.setTextColor(C.muted);
      doc.text('Ateliers merchandising non utilisés : rayon et parcours laissés par défaut (effet neutre).', M, y);
      y += 5.5;
    } else {
      const sc = (v) => (v == null ? 'non configuré' : `${v}/100`);
      doc.text(`Score planogramme : ${sc(mc.facingScore)}${mc.facingJour != null ? ` (posé jour ${mc.facingJour})` : ''} · Score parcours client : ${sc(mc.parcoursScore)}${mc.parcoursJour != null ? ` (posé jour ${mc.parcoursJour})` : ''}`, M, y);
      y += 5.5;
      const effets = [
        mc.traficPct ? `trafic ${mc.traficPct > 0 ? '+' : ''}${mc.traficPct} %` : null,
        mc.conversionPct ? `conversion ${mc.conversionPct > 0 ? '+' : ''}${mc.conversionPct} pt` : null,
        mc.panierPct ? `panier ${mc.panierPct > 0 ? '+' : ''}${mc.panierPct} %` : null,
      ].filter(Boolean);
      if (effets.length) { doc.text(`Effets quotidiens : ${effets.join(' · ')}`, M, y); y += 5.5; }
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(mc.impactPct >= 0 ? C.positive : C.warning);
      doc.text(`Impact estimé sur le CA : ${mc.impactPct > 0 ? '+' : ''}${mc.impactPct} % soit ${mc.impactEurMois > 0 ? '+' : ''}${fmtEur(mc.impactEurMois)}/mois`, M, y);
      y += 5.5;
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(C.muted);
    const lectureMc = !mc.configure
      ? "Lecture : un rayon et un parcours jamais travaillés laissent des ventes sur la table — le merchandising est le levier le moins cher du commerce."
      : (mc.scoreMoyen ?? 0) >= 75
        ? "Lecture : facings au prorata de la demande, marge au niveau des yeux et produit d'appel au fond — le magasin vend sans vendeur."
        : "Lecture : des marges de progrès sur le rayon ou le parcours — vérifiez la visibilité des familles sous-exposées et la place du produit d'appel.";
    const lmLines = doc.splitTextToSize(lectureMc, cw);
    ensure(lmLines.length * 4.4 + 6);
    doc.text(lmLines, M, y);
    y += lmLines.length * 4.4 + 4;
  }

  // ── Mécaniques retail : vos choix face à l'expert ──
  const mecas = r.mecaniques;
  if (mecas && mecas.length > 0) {
    ensure(24);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.primary);
    doc.text("MÉCANIQUES RETAIL — VOS CHOIX FACE À L'EXPERT", M, y);
    y += 6;
    mecas.forEach((m) => {
      const vousLines = doc.splitTextToSize(`Votre choix : ${m.choixLabel}`, cw - 4);
      const expLines = doc.splitTextToSize(`L'expert : ${m.expertLabel} — ${m.expertAvis}`, cw - 4);
      ensure(8 + (vousLines.length + expLines.length) * 4.4);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(C.text);
      doc.text(`• ${m.concept} (jour ${m.jour})${m.aligne ? '  — choix de l\'expert !' : ''}`, M, y);
      y += 4.8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(m.aligne ? C.positive : C.text);
      doc.text(vousLines, M + 4, y);
      y += vousLines.length * 4.2 + 1;
      doc.setTextColor(C.info);
      doc.text(expLines, M + 4, y);
      y += expLines.length * 4.2 + 3;
    });
    y += 3;
  }

  // ── Débrief ──
  y = sectionList(doc, M, y, cw, 'POINTS FORTS', C.positive, d.pointsForts);
  y = sectionList(doc, M, y, cw, 'POINTS À TRAVAILLER', C.warning, d.aTravailler);
  y = sectionList(doc, M, y, cw, 'CONSEILS POUR LA SUITE', C.info, d.conseils);

  // ── Pied de page ──
  doc.setDrawColor(C.light);
  doc.line(M, 282, W - M, 282);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(C.muted);
  doc.text(`SCD — Société de Commerce et de Distribution · Fiche générée le ${new Date().toLocaleDateString('fr-FR')}`, M, 287);

  const safe = (companyName || 'equipe').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  // 01/07 — output 'base64' : retourne le PDF encodé (envoi email formateur).
  if (output === 'base64') {
    return doc.output('datauristring').split(',')[1];
  }
  doc.save(`debrief-dgs-${safe}.pdf`);
}
